<template>
<div class="user-details-container">
    <!-- Refresh button -->
    <div class="refresh-container">
        <a href="#" class="brand w-nav-brand"><img sizes="84.03125px" alt="" :src="logo" loading="lazy" class="image-389" style="height: 45px;"></a>
        <button @click="fetchUserDetails" class="btn-refresh md-btn md-btn-primary">
            <i class="material-icons">refresh</i> Refresh
        </button>
    </div>

    <div v-if="loading" class="loading">
        <div class="md-loader"></div>
        <p>Loading user details...</p>
    </div>

    <div v-else-if="error" class="error md-card md-card-error">
        <i class="material-icons">error</i>
        <p>{{ error }}</p>
    </div>

    <div v-else-if="user" class="main-content">
        <!-- User Details Card -->
        <div class="user-card md-card">
            <h2 class="md-card-title">{{AppName}} - Save Customer</h2>

            <div class="user-info">
                <div class="info-row">
                    <span class="label">Name:</span>
                    <span class="value">{{ user.first_name }} {{ user.last_name }}</span>
                </div>

                <div class="info-row">
                    <span class="label">Email:</span>
                    <span class="value">{{ user.email }}</span>
                </div>

                <div v-if="user.phone" class="info-row">
                    <span class="label">Phone:</span>
                    <span class="value">{{ user.phone }}</span>
                </div>

                <div v-if="user.phone" class="info-row">
                    <span class="label">Cancellation Reason:</span>
                    <span class="value">Some Reason they cancelled for </span>
                </div>
            </div>
        </div>

        <!-- Cancellation Reason Card -->
        <div v-if="cancellationReason" class="cancellation-card md-card">
            <h3 class="md-card-subtitle">Cancellation Reason</h3>
            <div class="md-panel md-panel-warning">
                <div class="info-row">
                    <span class="label">Reason:</span>
                    <span class="value">{{ cancellationReason.reason }}</span>
                </div>
                <div v-if="cancellationReason.created_at" class="info-row">
                    <span class="label">Cancelled On:</span>
                    <span class="value">{{ formatDate(cancellationReason.created_at) }}</span>
                </div>
            </div>
        </div>

        <!-- Subscriptions List Card -->
        <div class="subscriptions-card md-card">
            <h3 class="md-card-title">Subscriptions</h3>

            <div v-if="subscriptions.length === 0" class="md-alert md-alert-error">
                <i class="material-icons">info</i>
                <p>No subscriptions found for this user.</p>
            </div>

            <div v-else class="subscriptions-list">
                <div v-for="subscription in subscriptions" :key="subscription.id" class="subscription-item md-panel">
                    <div class="subscription-header">
                        <h4>{{ subscription.plan_name }}</h4>
                        <span :class="['status-badge', getStatusClass(subscription.status)]">
                            {{ subscription.status }}
                        </span>
                    </div>

                    <div class="subscription-details">
                        <div class="info-row">
                            <span class="label">Amount:</span>
                            <span class="value">${{ subscription.amount }}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">Payment Terms:</span>
                            <span class="value">{{ getPaymentTermLabel(subscription.payment_terms) }}</span>
                        </div>
                        <div class="info-row">
                            <span class="label"> Next Billing Date:</span>
                            <span class="value">{{ formatDate(subscription.next_billing_date) }}</span>
                        </div>
                        <div v-if="subscription.created_at" class="info-row">
                            <span class="label">Created:</span>
                            <span class="value">{{ formatDate(subscription.created_at) }}</span>
                        </div>
                    </div>

                    <div v-if="subscription.status !== 'active'" class="subscription-actions">
                        <button @click="openActivateDialog(subscription)" class="md-btn md-btn-success">
                            <i class="material-icons">check_circle</i> Activate Subscription
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-else class="not-found">
        <p>User not found</p>
    </div>

    <!-- Activate Subscription Dialog -->
    <div v-if="showActivateDialog" class="dialog-overlay">
        <div class="dialog-content md-dialog">
            <div class="md-dialog-header">
                <h3>Activate Subscription</h3>
                <button @click="cancelActivate" class="md-dialog-close">
                    <i class="material-icons">close</i>
                </button>
            </div>

            <div class="md-dialog-body">
                <!-- Current Plan -->
                <div v-if="selectedSubscription" class="subscription-summary md-panel">
                    <h4>Current Plan: {{ selectedSubscription.plan_name }}</h4>
                    <p><strong>Amount:</strong> ${{ selectedSubscription.amount }}</p>
                    <p><strong>Payment Terms:</strong> {{ getPaymentTermLabel(selectedSubscription.payment_terms) }}</p>
                </div>

                <!-- Change Plan Toggle -->
                <div class="md-form-group">
                    <label>Do you want to change the plan?</label>
                    <div class="payment-method-toggle">
                        <label class="radio-label">
                            <input type="radio" v-model="wantChangePlan" :value="false" @change="onChangePlanToggle">
                            <span>No, keep current plan</span>
                        </label>
                        <label class="radio-label">
                            <input type="radio" v-model="wantChangePlan" :value="true" @change="onChangePlanToggle">
                            <span>Yes, change plan</span>
                        </label>
                    </div>
                </div>

                <!-- Available Plans dropdown (only shown when they want to change) -->
                <div v-if="wantChangePlan" class="md-form-group">
                    <label>Select Plan <span style="color: red;">*</span></label>
                    <select
                        v-if="availablePlans.length > 0"
                        v-model="selectedPlanId"
                        @change="onPlanSelect"
                        class="md-input-plain"
                    >
                        <option value="" disabled>-- Select a plan --</option>
                        <option
                            v-for="plan in availablePlans"
                            :key="plan.id"
                            :value="plan.id"
                        >
                            {{ plan.name }} - ${{ plan.price }} ({{ getPaymentTermLabel(plan.terms) }}){{ plan.id === selectedSubscription.plan_id ? ' [Current]' : '' }}
                        </option>
                    </select>
                    <div v-else-if="loadingPlans" style="text-align: center;">
                        <div class="md-loader" style="width: 24px; height: 24px; border-width: 3px;"></div>
                        <small>Loading plans...</small>
                    </div>
                </div>

                <div class="md-form-group">
                    <label for="activation-reason">Activation Reason <span style="color: red;">*</span></label>
                    <textarea
                        id="activation-reason"
                        v-model="activationForm.reason"
                        placeholder="Enter the reason for activating this subscription"
                        rows="3"
                        class="md-textarea"
                        required
                    ></textarea>
                </div>

                <div class="md-form-group">
                    <label for="next-billing-date">Next Billing Date (What will be the next billing date after this payment?)<span style="color: red;">*</span></label>
                    <input
                        id="next-billing-date"
                        type="date"
                        v-model="activationForm.next_billing_date_picker"
                        @input="updateDateFromPicker"
                        class="md-input-plain date-picker"
                    >
                    <small class="help-text">Use the calendar icon to select a date</small>
                </div>

                <div class="md-form-group">
                    <label for="coupon-code">Coupon Code (Optional)</label>

                    <div class="coupon-input-container">
                        <select
                            id="coupon-code"
                            v-model="couponCode"
                            @change="checkCoupon"
                            class="md-input-plain"
                            :class="{'invalid-coupon': couponError}"
                            :disabled="couponApplied || !allowedCoupons.length"
                            style="flex: 1; margin-right: 10px;"
                        >
                            <option value="" disabled>{{ allowedCoupons.length ? '-- Select coupon --' : 'No coupons for this plan' }}</option>
                            <option v-for="code in allowedCoupons" :key="code" :value="code">{{ code }}</option>
                        </select>
                        <button
                            v-if="couponApplied"
                            @click="removeCoupon"
                            class="remove-coupon-btn"
                            title="Remove Coupon"
                        >
                            <i class="material-icons">close</i>
                        </button>
                    </div>

                    <!-- Success message when coupon is applied -->
                    <div v-if="couponApplied && !couponError" class="coupon-message success-message">
                        <i class="material-icons">check_circle</i>
                        <span>{{ couponCode }} - ${{ discount }} off</span>
                    </div>

                    <!-- Error message when coupon is invalid -->
                    <div v-if="couponError" class="coupon-message error-message">
                        <i class="material-icons">error</i>
                        <span>{{ couponErrorMessage || 'This coupon is not set up for this plan.' }}</span>
                    </div>
                </div>

                <!-- Payment Method Section -->
                <div class="section-divider">
                    <h4 class="section-title"><i class="material-icons">credit_card</i> Payment Method</h4>
                </div>

                <div class="md-form-group">
                    <div class="payment-method-toggle">
                        <label class="radio-label">
                            <input type="radio" v-model="useCardOnFile" :value="true" @change="onPaymentMethodChange">
                            <span>Use payment method on record</span>
                        </label>
                        <label class="radio-label">
                            <input type="radio" v-model="useCardOnFile" :value="false" @change="onPaymentMethodChange">
                            <span>Enter new card details</span>
                        </label>
                    </div>
                </div>

                <div v-if="!useCardOnFile">
                    <div class="md-form-group">
                        <label>Enter Card <span style="color: red;">*</span></label>
                        <div id="activate-card-element" class="ns-card-element" ref="activateCardElement"></div>
                    </div>

                    <!-- Billing Address Section -->
                    <div class="section-divider">
                        <h4 class="section-title"><i class="material-icons">location_on</i> Billing Address</h4>
                    </div>

                    <div class="md-form-group">
                        <label>Billing Address <span style="color: red;">*</span></label>
                        <google-places-autocomplete :api-key="googleMapsApiKey" @address-updated="getBillingAddress" ref="billingAutocomplete" />
                    </div>
                </div>
            </div>

            <div class="md-dialog-footer">
                <button @click="cancelActivate" class="md-btn md-btn-secondary" :disabled="submitting">Cancel</button>
                <button @click="submitActivation" class="md-btn md-btn-success" :disabled="!canSubmit || submitting">
                    <i class="material-icons">check</i> {{ submitting ? 'Activating...' : 'Activate' }}
                </button>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import {
    BasisTheory
} from "@basis-theory/basis-theory-js";

const bt = await new BasisTheory().init(process.env.VUE_APP_BASIS, {
    elements: true
});

import GooglePlacesAutocomplete from './GooglePlacesAutocomplete.vue'
import { couponsForTerms } from '../services/coupons'

export default {
    name: 'SubscriptionChange',
    components: {
        GooglePlacesAutocomplete
    },
    data() {
        return {
            user: null,
            subscriptions: [],
            cancellationReason: null,
            loading: true,
            error: null,
            logo: process.env.VUE_APP_LOGO,
            AppName: process.env.VUE_APP_NAME,
            showActivateDialog: false,
            selectedSubscription: null,
            submitting: false,
            activationForm: {
                reason: '',
                next_billing_date: '',
                next_billing_date_picker: ''
            },
            couponCode: '',
            couponApplied: false,
            couponError: false,
            // Backend reason when it gives one (e.g. a limited coupon is used up).
            couponErrorMessage: '',
            couponId: null,
            discount: 0,
            allPlans: [],
            loadingPlans: false,
            selectedPlanId: null,
            selectedPlan: null,
            wantChangePlan: false,
            useCardOnFile: true,
            cardElement: null,
            cardToken: '',
            cardBin: '',
            cardLast4: '',
            cardBrand: '',
            googleMapsApiKey: process.env.VUE_APP_GOOGLE_KEY,
            billingAddress: {
                street: '',
                address_line2: '',
                city: '',
                state: '',
                zip: ''
            }
        }
    },
    computed: {
        canSubmit() {
            return this.activationForm.reason.trim() !== '' &&
                   this.activationForm.next_billing_date.trim() !== '' &&
                   this.selectedPlanId;
        },
        // Same terms checkCoupon() validates against.
        couponTerms() {
            if (this.selectedPlan) return this.selectedPlan.terms;
            return this.selectedSubscription ? this.selectedSubscription.payment_terms : null;
        },
        allowedCoupons() {
            return couponsForTerms(this.couponTerms);
        },
        availablePlans() {
            if (!this.selectedSubscription || !this.allPlans.length) return [];
            const excludeTerms = [0, 2]; // Exclude one-time purchases and 2-month terms
            const sub = this.selectedSubscription;
            const baseFilter = plan => plan.is_active && !excludeTerms.includes(plan.terms);
            if (sub.product_id) {
                return this.allPlans.filter(plan => baseFilter(plan) && plan.product === sub.product_id);
            }
            // Fallback: match by the product name prefix (e.g. "Semaglutide" from "Semaglutide - Monthly")
            const productName = sub.plan_name?.split(' - ')[0]?.toLowerCase();
            if (!productName) return [];
            return this.allPlans.filter(plan =>
                baseFilter(plan) && plan.name?.toLowerCase().startsWith(productName)
            );
        }
    },
    async created() {
        const userId = this.$route.params.id;
        this.fetchUserDetails(userId);
        this.fetchAllPlans();
    },
    mounted() {
        // Load Material Icons if not already loaded
        if (!document.querySelector('link[href*="material-icons"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
            document.head.appendChild(link);
        }
    },
    methods: {
        async fetchUserDetails(userId = null) {
            this.loading = true;
            this.error = null;
            const id = userId || this.$route.params.id;

            try {
                // Fetch user basic details
                const userResponse = await this.$http.get(`/users/${id}`);
                this.user = userResponse.data.user;

                // Fetch subscription status and cancellation reason
                const subsResponse = await this.$http.get(`/user-subs-status/${id}`);

                if (subsResponse.data.status === 'success') {
                    this.subscriptions = subsResponse.data.subscription || [];
                    this.cancellationReason = subsResponse.data.cancelation_reason;
                } else {
                    this.error = 'Failed to load subscription details.';
                }

                // Data refreshed successfully
            } catch (err) {
                console.error('Error fetching user details:', err);
                this.error = err.response?.data?.message || 'Failed to load user details. Please try again later.';
            } finally {
                this.loading = false;
            }
        },

        openActivateDialog(subscription) {
            this.selectedSubscription = subscription;
            this.showActivateDialog = true;
            // Reset form and coupon
            this.activationForm = {
                reason: '',
                next_billing_date: '',
                next_billing_date_picker: ''
            };
            this.couponCode = '';
            this.couponApplied = false;
            this.couponError = false;
            this.couponId = null;
            this.discount = 0;
            this.useCardOnFile = true;
            this.wantChangePlan = false;
            this.cardElement = null;
            this.cardToken = '';
            this.cardBin = '';
            this.cardLast4 = '';
            this.cardBrand = '';
            this.billingAddress = { street: '', address_line2: '', city: '', state: '', zip: '' };
            // Default to current plan
            this.selectedPlanId = subscription.plan_id;
            this.selectedPlan = {
                id: subscription.plan_id,
                name: subscription.plan_name,
                price: subscription.amount,
                terms: subscription.payment_terms
            };
            // Auto-calculate next billing date based on current plan terms
            this.calculateNextBillingDate(subscription.payment_terms);
        },

        cancelActivate() {
            this.showActivateDialog = false;
            this.selectedSubscription = null;
            this.activationForm = {
                reason: '',
                next_billing_date: '',
                next_billing_date_picker: ''
            };
            this.couponCode = '';
            this.couponApplied = false;
            this.couponError = false;
            this.couponId = null;
            this.discount = 0;
            this.useCardOnFile = true;
            this.wantChangePlan = false;
            this.cardElement = null;
            this.cardToken = '';
            this.cardBin = '';
            this.cardLast4 = '';
            this.cardBrand = '';
            this.billingAddress = { street: '', address_line2: '', city: '', state: '', zip: '' };
        },

        updateDateFromPicker() {
            if (this.activationForm.next_billing_date_picker) {
                this.activationForm.next_billing_date = this.activationForm.next_billing_date_picker;
            } else {
                this.activationForm.next_billing_date = '';
            }
        },

        async checkCoupon() {
            if (!this.couponCode.trim()) return;
            if (this.couponApplied) return;

            this.couponError = false;
            this.couponErrorMessage = '';

            try {
                const response = await this.$http.post('/validate-coupon', {
                    coupon: this.couponCode,
                    payment_terms: this.couponTerms
                });

                console.log('Coupon validation response:', response.data);

                if (response.data && response.data.status === 'success') {
                    this.couponApplied = true;
                    this.couponId = response.data.coupon_id;
                    this.discount = response.data.c_val;
                } else {
                    console.log('Coupon validation failed - status not success');
                    this.couponError = true;
                    this.couponErrorMessage = response.data && response.data.message || '';
                }
            } catch (error) {
                console.error('Error validating coupon:', error);
                console.error('Error response:', error.response);
                this.couponError = true;
            }
        },

        async fetchAllPlans() {
            this.loadingPlans = true;
            try {
                const response = await this.$http.get('/get-all-plans');
                if (response.data && response.data.plans) {
                    this.allPlans = response.data.plans;
                } else if (Array.isArray(response.data)) {
                    this.allPlans = response.data;
                }
            } catch (error) {
                console.error('Error fetching plans:', error);
            } finally {
                this.loadingPlans = false;
            }
        },

        onChangePlanToggle() {
            if (!this.wantChangePlan) {
                // Reset to current plan
                const sub = this.selectedSubscription;
                this.selectedPlanId = sub.plan_id;
                this.selectedPlan = {
                    id: sub.plan_id,
                    name: sub.plan_name,
                    price: sub.amount,
                    terms: sub.payment_terms
                };
                this.calculateNextBillingDate(sub.payment_terms);
                this.revalidateCouponForTerms();
            } else {
                // Clear selection so they must pick
                this.selectedPlanId = '';
                this.selectedPlan = null;
            }
        },

        onPlanSelect() {
            const plan = this.availablePlans.find(p => p.id === this.selectedPlanId);
            if (plan) {
                this.selectedPlan = plan;
                this.calculateNextBillingDate(plan.terms);
                this.revalidateCouponForTerms();
            }
        },

        selectPlan(plan) {
            this.selectedPlanId = plan.id;
            this.selectedPlan = plan;
            this.calculateNextBillingDate(plan.terms);
            this.revalidateCouponForTerms();
        },

        // A coupon is validated server-side against specific payment_terms, and
        // checkCoupon() won't re-run while couponApplied is set — without this,
        // a plan change keeps the coupon (and its discount) validated against
        // the old terms all the way to submit. Must run AFTER selectedPlan is
        // updated so the new terms go out in the request.
        revalidateCouponForTerms() {
            if (!this.couponApplied && !this.couponCode.trim()) return;
            this.couponApplied = false;
            this.couponError = false;
            this.couponId = null;
            this.discount = 0;
            // The new plan may cap below the picked coupon (e.g. 75OFF → 3-month).
            if (!this.allowedCoupons.includes(this.couponCode)) {
                this.couponCode = '';
                return;
            }
            this.checkCoupon();
        },

        calculateNextBillingDate(terms) {
            if (!terms || terms <= 0) return;
            const date = new Date();
            date.setMonth(date.getMonth() + terms);
            // Format as YYYY-MM-DD for the date picker
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            this.activationForm.next_billing_date_picker = `${year}-${month}-${day}`;
            this.activationForm.next_billing_date = `${year}-${month}-${day}`;
        },

        onPaymentMethodChange() {
            if (!this.useCardOnFile) {
                // Mount card element when switching to new card
                this.$nextTick(() => {
                    setTimeout(() => { this.createCardElement(); }, 100);
                });
            }
        },

        getBillingAddress(address) {
            this.billingAddress = {
                street: address.street || '',
                address_line2: address.address_line2 || '',
                city: address.city || '',
                state: address.state || '',
                zip: address.zip || ''
            };
        },

        async createCardElement() {
            if (bt && this.$refs.activateCardElement) {
                try {
                    this.cardElement = bt.createElement('card');
                    await this.cardElement.mount('#activate-card-element');

                    this.cardElement.on("change", (event) => {
                        if (event.cardBrand) {
                            this.cardBrand = event.cardBrand;
                        }
                        if (event.cardBin && event.cardBin.length >= 6) {
                            this.cardBin = event.cardBin;
                        }
                        if (event.cardLast4) {
                            this.cardLast4 = event.cardLast4;
                        }
                    });
                } catch (error) {
                    console.error('Error mounting card element:', error);
                }
            }
        },

        removeCoupon() {
            this.couponCode = '';
            this.couponApplied = false;
            this.couponError = false;
            this.couponId = null;
            this.discount = 0;
        },

        async submitActivation() {
            if (!this.canSubmit || this.submitting) return;

            this.submitting = true;

            try {
                const plan = this.selectedPlan;
                const payload = {
                    subsId: this.selectedSubscription.id,
                    planId: plan.id,
                    planName: plan.name,
                    amount: plan.price,
                    paymentTerms: plan.terms,
                    next_billing_date: this.activationForm.next_billing_date,
                    reason: this.activationForm.reason
                };

                // Add coupon ID to payload if a coupon has been applied
                if (this.couponApplied && this.couponId) {
                    payload.couponId = this.couponId;
                }

                payload.useCardOnFile = this.useCardOnFile;

                if (!this.useCardOnFile) {
                    // Tokenize new card via Basis Theory
                    try {
                        const token = await bt.tokens.create({
                            type: "card",
                            data: this.cardElement,
                        });
                        payload.cardToken = token.id;
                        payload.cardDetails = {
                            ...token.data,
                            cardBin: this.cardBin,
                            cardBrand: this.cardBrand,
                            cardLast4: this.cardLast4
                        };
                    } catch (tokenError) {
                        console.error('Error tokenizing card:', tokenError);
                        this.submitting = false;
                        this.error = "Invalid card details. Please check and try again.";
                        return;
                    }

                    payload.billingAddress = { ...this.billingAddress };
                }

                const response = await this.$http.post('/user-subs-status', payload);

                if (response.data.status === 'success') {
                    // Close dialog first
                    this.showActivateDialog = false;
                    this.submitting = false;

                    // Reset form, coupon, and refresh data
                    this.selectedSubscription = null;
                    this.activationForm = {
                        reason: '',
                        next_billing_date: '',
                        next_billing_date_picker: ''
                    };
                    this.couponCode = '';
                    this.couponApplied = false;
                    this.couponError = false;
                    this.couponId = null;
                    this.discount = 0;
                    this.cardElement = null;
                    this.cardToken = '';
                    this.cardBin = '';
                    this.cardLast4 = '';
                    this.cardBrand = '';
                    this.billingAddress = { street: '', address_line2: '', city: '', state: '', zip: '' };
                    await this.fetchUserDetails();
                } else {
                    this.submitting = false;
                    this.error = response.data.message || 'Failed to activate subscription.';
                }
            } catch (error) {
                console.error('Error activating subscription:', error);
                this.submitting = false;
                this.error = error.response?.data?.message || error.response?.data?.errors || 'Failed to activate subscription. Please try again.';
            }
        },

        formatDate(dateString) {
            if (!dateString) return 'N/A';
            try {
                const date = new Date(dateString);
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            } catch (e) {
                return dateString;
            }
        },

        getPaymentTermLabel(terms) {
            const labels = {
                0: 'One Time',
                1: 'Monthly',
                3: 'Quarterly',
                6: 'Semi Annual',
                12: 'Annual'
            };
            return labels[terms] || `${terms} months`;
        },

        getStatusClass(status) {
            const classes = {
                'active': 'status-active',
                'cancelled': 'status-cancelled',
                'expired': 'status-expired',
                'pending': 'status-pending'
            };
            return classes[status.toLowerCase()] || 'status-default';
        }
    }
}
</script>

<style scoped>
/* Material Design inspired styles */
.user-details-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    position: relative;
    font-family: 'Roboto', Arial, sans-serif;
}

.refresh-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.btn-refresh {
    display: flex;
    align-items: center;
}

.loading,
.error,
.not-found {
    text-align: center;
    padding: 20px;
}

.md-loader {
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top: 4px solid #3498db;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 15px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

/* Card Styles */
.md-card {
    background-color: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
    transition: box-shadow 0.3s ease;
}

.md-card:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.md-card-title {
    font-size: 1.5rem;
    font-weight: 500;
    color: #333;
    margin-top: 0;
    margin-bottom: 24px;
    text-align: center;
}

.md-card-subtitle {
    font-size: 1.2rem;
    font-weight: 500;
    color: #555;
    margin-top: 0;
    margin-bottom: 16px;
}

.md-card-error {
    background-color: #ffebee;
    color: #d32f2f;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* Alert Styles */
.md-alert {
    padding: 16px;
    border-radius: 4px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
}

.md-alert i {
    margin-right: 12px;
    font-size: 24px;
}

.md-alert-error {
    background-color: #ffebee;
    color: #d32f2f;
    border-left: 4px solid #f44336;
}

/* Panel Styles */
.md-panel {
    background-color: #f5f5f5;
    border-radius: 4px;
    padding: 16px;
    margin-bottom: 16px;
}

.md-panel-warning {
    background-color: #FFF8E1;
    border-left: 4px solid #FFC107;
}

.user-info {
    margin-bottom: 0;
}

.info-row {
    display: flex;
    padding: 12px 0;
    border-bottom: 1px solid #eee;
    align-items: flex-start;
}

.info-row:last-child {
    border-bottom: none;
}

.label {
    font-weight: 500;
    min-width: 150px;
    width: 150px;
    color: #555;
    flex-shrink: 0;
}

.value {
    flex: 1;
    color: #333;
    word-break: break-word;
}

/* Subscriptions List */
.subscriptions-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.subscription-item {
    background-color: white;
    border: 1px solid #e0e0e0;
    padding: 20px;
}

.subscription-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid #eee;
}

.subscription-header h4 {
    margin: 0;
    font-size: 1.2rem;
    color: #333;
}

.status-badge {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: 500;
    text-transform: uppercase;
}

.status-active {
    background-color: #E8F5E9;
    color: #2E7D32;
}

.status-cancelled {
    background-color: #FFEBEE;
    color: #C62828;
}

.status-expired {
    background-color: #FFF3E0;
    color: #E65100;
}

.status-pending {
    background-color: #E3F2FD;
    color: #1565C0;
}

.status-default {
    background-color: #F5F5F5;
    color: #757575;
}

.subscription-details {
    margin-bottom: 16px;
}

.subscription-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #eee;
}

/* Button Styles */
.md-btn {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: background-color 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.md-btn i {
    margin-right: 6px;
}

.md-btn:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.md-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
}

.md-btn-primary {
    background-color: #2196F3;
    color: white;
}

.md-btn-secondary {
    background-color: #757575;
    color: white;
}

.md-btn-success {
    background-color: #4CAF50;
    color: white;
}

/* Dialog Styles */
.dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.md-dialog {
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    max-height: 90vh;
    overflow: hidden;
}

.md-dialog-header {
    padding: 20px 24px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.md-dialog-header h3 {
    margin: 0;
    font-weight: 500;
    font-size: 1.2rem;
    color: #333;
}

.md-dialog-close {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #757575;
    padding: 4px;
    border-radius: 50%;
    display: flex;
}

.md-dialog-close:hover {
    background-color: #f1f1f1;
}

.md-dialog-body {
    padding: 24px;
    overflow-y: auto;
}

.md-dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 16px 24px;
    border-top: 1px solid #eee;
}

/* Form Styles */
.md-form-group {
    margin-bottom: 20px;
}

.md-form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #555;
    font-size: 0.9rem;
}

.md-input-plain,
.md-textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
}

.md-input-plain:focus,
.md-textarea:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

.date-picker {
    cursor: pointer;
    font-family: inherit;
}

.date-picker::-webkit-calendar-picker-indicator {
    cursor: pointer;
    font-size: 1.2rem;
}

.md-textarea {
    resize: vertical;
    min-height: 80px;
    font-family: inherit;
}

.help-text {
    display: block;
    margin-top: 4px;
    font-size: 0.85rem;
    color: #757575;
}

/* Coupon Styles */
.coupon-input-container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px;
}

.invalid-coupon {
    border-color: #f44336 !important;
}

.remove-coupon-btn {
    background-color: #f44336;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s ease;
    color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.remove-coupon-btn:hover {
    background-color: #d32f2f;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.remove-coupon-btn i {
    font-size: 18px;
    margin: 0;
}

.coupon-message {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 4px;
    margin-top: 10px;
    font-size: 0.9rem;
}

.coupon-message i {
    font-size: 18px;
}

.success-message {
    background-color: #E8F5E9;
    color: #2E7D32;
    border-left: 4px solid #4CAF50;
}

.error-message {
    background-color: #FFEBEE;
    color: #C62828;
    border-left: 4px solid #F44336;
}

.subscription-summary {
    background-color: #E3F2FD;
    margin-bottom: 20px;
}

.subscription-summary h4 {
    margin-top: 0;
    color: #1565C0;
}

.subscription-summary p {
    margin: 8px 0;
    color: #333;
}

/* Responsive Design */
@media (max-width: 768px) {
    .user-details-container {
        padding: 15px;
    }

    .refresh-container {
        flex-direction: column;
        gap: 15px;
        align-items: stretch;
    }

    .md-card {
        padding: 16px;
    }

    .info-row {
        flex-direction: column;
        gap: 5px;
    }

    .label {
        min-width: auto;
        width: auto;
        font-weight: 600;
    }

    .subscription-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }

    .form-row {
        flex-direction: column;
        gap: 0;
    }
}

/* Basis Theory Card Element */
.ns-card-element {
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 12px;
    min-height: 40px;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.ns-card-element:focus-within {
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

/* Section Divider */
.section-divider {
    margin-top: 24px;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e0e0e0;
}

.section-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 500;
    color: #333;
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-title i {
    font-size: 20px;
    color: #2196F3;
}

/* Form Row for side-by-side fields */
.form-row {
    display: flex;
    gap: 16px;
}

.form-col {
    flex: 1;
}

/* Payment Method Toggle */
.payment-method-toggle {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.radio-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 0.95rem;
    color: #333;
}

.radio-label input[type="radio"] {
    accent-color: #2196F3;
    width: 18px;
    height: 18px;
    cursor: pointer;
}

/* Plan Cards Grid */
.plans-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
    margin-top: 8px;
}

.plan-card {
    position: relative;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px 12px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: #fff;
}

.plan-card:hover {
    border-color: #2196F3;
    box-shadow: 0 2px 8px rgba(33, 150, 243, 0.15);
}

.plan-card-selected {
    border-color: #2196F3;
    background-color: #E3F2FD;
    box-shadow: 0 2px 8px rgba(33, 150, 243, 0.25);
}

.plan-card-current {
    border-color: #4CAF50;
}

.plan-card-current.plan-card-selected {
    border-color: #2196F3;
}

.plan-card-name {
    font-weight: 600;
    font-size: 0.85rem;
    color: #333;
    margin-bottom: 8px;
}

.plan-card-price {
    font-size: 1.3rem;
    font-weight: 700;
    color: #1565C0;
    margin-bottom: 4px;
}

.plan-card-terms {
    font-size: 0.8rem;
    color: #757575;
}

.plan-card-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: #4CAF50;
    color: white;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    text-transform: uppercase;
}
</style>
